/**
 * HreflangTags — global <link rel="alternate" hreflang="..."> manager.
 *
 * Mounted once inside <BrowserRouter> in App.js. On every route change,
 * ensures three (fr-FR / fr-CM / en-US / x-default) alternate <link>
 * tags exist and point to the correct URL variants for the current
 * route.
 *
 * Strategy
 * ========
 * - Realty237 serves all languages from the same URL and switches locale
 *   at runtime via a `?lang=en` query-param. Google supports this
 *   pattern as long as the alternate URLs, once followed, render the
 *   correct locale.
 * - We keep the alternates in sync with the CanonicalUrl helper: the
 *   canonical path (without tracking params) is the base for fr-FR and
 *   x-default; en-US is the same path with `?lang=en` appended.
 *
 * Refs:
 *   - https://developers.google.com/search/docs/specialty/international/localized-versions
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const MARKER = 'data-realty237-hreflang';

const HREFLANGS = ['fr-FR', 'fr-CM', 'en-US', 'x-default'];

function ensureLink(hreflang, href) {
  let el = document.head.querySelector(`link[${MARKER}][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    el.setAttribute(MARKER, '');
    document.head.appendChild(el);
  }
  if (el.getAttribute('href') !== href) el.setAttribute('href', href);
}

const HreflangTags = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const origin = window.location.origin;
    const params = new URLSearchParams(search || '');
    params.delete('lang'); // never stack lang=
    // Strip tracking params from hreflang URLs (utm_*, fbclid, gclid, ref)
    Array.from(params.keys()).forEach((k) => {
      if (/^utm_/i.test(k) || ['fbclid', 'gclid', 'ref'].includes(k)) {
        params.delete(k);
      }
    });
    const baseQs = params.toString();
    const base = `${origin}${pathname}${baseQs ? `?${baseQs}` : ''}`;

    const enParams = new URLSearchParams(baseQs);
    enParams.set('lang', 'en');
    const enUrl = `${origin}${pathname}?${enParams.toString()}`;

    ensureLink('fr-FR', base);
    ensureLink('fr-CM', base);
    ensureLink('en-US', enUrl);
    ensureLink('x-default', base);
  }, [pathname, search]);

  return null;
};

// Exported for tests
export { HREFLANGS };
export default HreflangTags;
